import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Mail,
  Play,
  FileText,
  Upload,
  Info,
  Eye,
  CheckCircle2,
  X,
  Copy,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ListFilter,
  HelpCircle,
  Calendar,
  AlertTriangle,
} from 'lucide-react';
import {
  describeShowWhen,
  showWhenAnswerValues,
  showWhenProblemFor,
} from '../../../lib/phaseActionVisibility';
import {
  PhaseActionType,
  ProgramPhaseActionDef,
  STANDARD_PHASES,
  StandardPhaseKey,
} from '../../../types/programPhases';
import { ProgramConfig } from '../../../lib/programConfig';
import { safeFetch } from '../../../lib/api';
import SearchableSelect from '../../shared/SearchableSelect';

interface ProgramPhasesEditorProps {
  programId: string;
  allPrograms?: ProgramConfig[];
  onToast?: (message: string, type?: string) => void;
}

const ACTION_TYPE_OPTIONS: Array<{
  type: PhaseActionType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { type: 'select_one_option', label: 'Select One Option', icon: ListFilter },
  { type: 'yes_no', label: 'Yes / No Choice', icon: HelpCircle },
  { type: 'date_entry', label: 'Date Entry', icon: Calendar },
  { type: 'send_email', label: 'Send Email', icon: Mail },
  { type: 'send_automation', label: 'Send Automation', icon: Play },
  { type: 'text_input', label: 'Text Input', icon: FileText },
  { type: 'file_input', label: 'File Input', icon: Upload },
  { type: 'visual_text', label: 'Visual Text Display', icon: Info },
  { type: 'visual_file', label: 'Visual File — View Only', icon: Eye },
];

export const ProgramPhasesEditor: React.FC<ProgramPhasesEditorProps> = ({
  programId,
  allPrograms = [],
  onToast,
}) => {
  const [activePhaseKey, setActivePhaseKey] = useState<StandardPhaseKey>('draft');
  const [actions, setActions] = useState<ProgramPhaseActionDef[]>([]);
  const [stageNudges, setStageNudges] = useState<
    Record<string, { emailTemplateId?: string; documentTemplateId?: string }>
  >({});
  const [automationsList, setAutomationsList] = useState<
    Array<{ id: string; name: string; status?: string }>
  >([]);
  const [emailTemplatesList, setEmailTemplatesList] = useState<Array<{ id: string; name: string }>>([]);
  const [documentTemplatesList, setDocumentTemplatesList] = useState<Array<{ id: string; name: string; code?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Load failures are shown, not swallowed: an empty editor used to mean
  // both "nothing configured" and "the request was rejected".
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  // Copy modal state
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [copyScope, setCopyScope] = useState<'all' | 'single'>('all');
  const [copyMode, setCopyMode] = useState<'replace' | 'append'>('replace');
  const [selectedSourceProgramId, setSelectedSourceProgramId] = useState<string>('');
  const [selectedSourcePhaseKey, setSelectedSourcePhaseKey] = useState<StandardPhaseKey>('draft');
  const [targetPhaseKey, setTargetPhaseKey] = useState<StandardPhaseKey>('draft');
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    fetchActions();
    fetchAutomationsAndTemplates();
  }, [programId]);

  const fetchActions = async () => {
    setLoading(true);
    try {
      const data = await safeFetch(`/api/admin/programs/${programId}/phase-actions`);
      setActions(data.actions || []);
      if (data.stageNudges && typeof data.stageNudges === 'object') {
        setStageNudges(data.stageNudges);
      }
      setLoadError(null);
    } catch (err: any) {
      console.error('Failed to fetch phase actions:', err);
      setLoadError(err.message || 'Failed to load phase actions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomationsAndTemplates = async () => {
    const [autoRes, emailRes, docRes] = await Promise.allSettled([
      safeFetch('/api/admin/automations'),
      safeFetch('/api/email-templates'),
      safeFetch('/api/admin/document-templates'),
    ]);
    if (autoRes.status === 'fulfilled' && Array.isArray(autoRes.value.automations)) {
      setAutomationsList(autoRes.value.automations);
    }
    if (emailRes.status === 'fulfilled' && Array.isArray(emailRes.value.templates)) {
      setEmailTemplatesList(emailRes.value.templates);
    }
    if (docRes.status === 'fulfilled' && Array.isArray(docRes.value.templates)) {
      setDocumentTemplatesList(docRes.value.templates);
    }
    const firstFailure = [autoRes, emailRes, docRes].find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    setPickerError(
      firstFailure ? firstFailure.reason?.message || 'Failed to load pickers' : null
    );
  };

  const handleAddAction = (actionType: PhaseActionType) => {
    const initialConfig: any = {};
    if (actionType === 'select_one_option') {
      initialConfig.options = [
        {
          id: `opt_${Date.now()}_1`,
          label: 'Option 1: Send Acceptance Email',
          actionType: 'send_email',
        },
        {
          id: `opt_${Date.now()}_2`,
          label: 'Option 2: Send Rejection Email',
          actionType: 'send_email',
        },
      ];
      initialConfig.requireNote = true;
    } else if (actionType === 'yes_no') {
      initialConfig.yesLabel = 'Yes';
      initialConfig.noLabel = 'No';
    } else if (actionType === 'date_entry') {
      initialConfig.requireDate = true;
    }

    const newAct: ProgramPhaseActionDef = {
      id: `act_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      phaseKey: activePhaseKey,
      actionType,
      title: `New ${actionType.replace(/_/g, ' ')}`,
      instructions: '',
      required: true,
      requireNote: actionType === 'select_one_option' || false,
      sortOrder: actions.filter((a) => a.phaseKey === activePhaseKey).length + 1,
      config: initialConfig,
    };
    setActions([...actions, newAct]);
  };

  const handleUpdateAction = (id: string, updates: Partial<ProgramPhaseActionDef>) => {
    setActions(actions.map((a) => (a.id === id ? { ...a, ...updates } : a)));
  };

  const handleRemoveAction = (id: string) => {
    setActions(actions.filter((a) => a.id !== id));
  };

  const handleExecuteCopy = async () => {
    if (!selectedSourceProgramId) {
      setCopyError('Please select a source program to copy tasks from.');
      return;
    }

    setCopying(true);
    setCopyError(null);

    try {
      const data = await safeFetch(`/api/admin/programs/${selectedSourceProgramId}/phase-actions`);
      const sourceActions: ProgramPhaseActionDef[] = data.actions || [];

      if (sourceActions.length === 0) {
        setCopyError('The selected source program has no phase tasks configured.');
        return;
      }

      if (copyScope === 'all') {
        // References between tasks (prerequisites, showWhen sources)
        // must be remapped to the copied tasks' NEW ids — a copied
        // condition pointing at the source program's task id would be
        // broken on arrival and the save-time validation would refuse
        // the whole configuration.
        const idMap = new Map(
          sourceActions.map((act) => [
            act.id,
            `act_${Date.now()}_${Math.random().toString(36).substring(2, 6)}_${act.id}`,
          ])
        );
        const remapRefs = (act: ProgramPhaseActionDef): ProgramPhaseActionDef => ({
          ...act,
          id: idMap.get(act.id)!,
          prerequisiteActionDefIds: act.prerequisiteActionDefIds?.map((id) => idMap.get(id) ?? id),
          showWhen: act.showWhen
            ? {
                ...act.showWhen,
                sourceActionDefId: idMap.get(act.showWhen.sourceActionDefId) ?? act.showWhen.sourceActionDefId,
              }
            : undefined,
        });
        let copiedList: ProgramPhaseActionDef[] = sourceActions.map(remapRefs);

        if (copyMode === 'replace') {
          setActions(copiedList);
        } else {
          setActions([...actions, ...copiedList]);
        }

        const sourceProgName = allPrograms.find((p) => p.id === selectedSourceProgramId)?.name || 'source program';
        onToast?.(`Copied ${copiedList.length} admin task(s) from all stages of ${sourceProgName}`, 'success');
      } else {
        // Single stage copy
        const stageSourceActions = sourceActions.filter((a) => a.phaseKey === selectedSourcePhaseKey);

        if (stageSourceActions.length === 0) {
          const sourcePhaseLabel = STANDARD_PHASES.find((p) => p.key === selectedSourcePhaseKey)?.label || selectedSourcePhaseKey;
          setCopyError(`The selected source program has no tasks configured in ${sourcePhaseLabel}.`);
          return;
        }

        const targetExistingInStage = actions.filter((a) => a.phaseKey === targetPhaseKey);
        const startSortOrder = copyMode === 'append' ? targetExistingInStage.length : 0;

        const stageIdMap = new Map(
          stageSourceActions.map((act) => [
            act.id,
            `act_${Date.now()}_${Math.random().toString(36).substring(2, 6)}_${act.id}`,
          ])
        );
        const copiedStageActions: ProgramPhaseActionDef[] = stageSourceActions.map((act, index) => ({
          ...act,
          id: stageIdMap.get(act.id)!,
          phaseKey: targetPhaseKey,
          sortOrder: startSortOrder + index + 1,
          prerequisiteActionDefIds: act.prerequisiteActionDefIds?.map((id) => stageIdMap.get(id) ?? id),
          showWhen: act.showWhen
            ? {
                ...act.showWhen,
                sourceActionDefId:
                  stageIdMap.get(act.showWhen.sourceActionDefId) ?? act.showWhen.sourceActionDefId,
              }
            : undefined,
        }));

        if (copyMode === 'replace') {
          const remainingActions = actions.filter((a) => a.phaseKey !== targetPhaseKey);
          setActions([...remainingActions, ...copiedStageActions]);
        } else {
          setActions([...actions, ...copiedStageActions]);
        }

        const sourcePhaseLabel = STANDARD_PHASES.find((p) => p.key === selectedSourcePhaseKey)?.label || selectedSourcePhaseKey;
        const targetPhaseLabel = STANDARD_PHASES.find((p) => p.key === targetPhaseKey)?.label || targetPhaseKey;
        const sourceProgName = allPrograms.find((p) => p.id === selectedSourceProgramId)?.name || 'source program';

        onToast?.(
          `Copied ${copiedStageActions.length} task(s) from ${sourceProgName} (${sourcePhaseLabel}) into ${targetPhaseLabel}`,
          'success'
        );
      }

      setIsCopyModalOpen(false);
    } catch (err: any) {
      console.error('Failed to copy phase actions:', err);
      setCopyError(err.message || 'Failed to copy phase actions from selected program.');
    } finally {
      setCopying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await safeFetch(`/api/admin/programs/${programId}/phase-actions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actions, stageNudges }),
      });
      onToast?.('Program phase administrative actions saved', 'success');
    } catch (err: any) {
      onToast?.(`Failed to save phase actions: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const phaseActions = actions.filter((a) => a.phaseKey === activePhaseKey);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // ADM-11: one reorder operation shared by drag and by the per row move
  // up / move down buttons, so both paths mutate state identically and
  // persist through the same handleSave PUT.
  const [moveAnnouncement, setMoveAnnouncement] = useState('');

  const moveAction = (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= phaseActions.length ||
      toIndex >= phaseActions.length
    ) {
      return;
    }

    const currentPhaseList = [...phaseActions];
    const [movedItem] = currentPhaseList.splice(fromIndex, 1);
    currentPhaseList.splice(toIndex, 0, movedItem);

    // Recalculate sortOrder for tasks in current phase
    const reorderedPhaseList = currentPhaseList.map((item, idx) => ({
      ...item,
      sortOrder: idx + 1,
    }));

    // Reconstruct full actions array preserving other phases
    const otherPhasesActions = actions.filter((a) => a.phaseKey !== activePhaseKey);
    setActions([...otherPhasesActions, ...reorderedPhaseList]);
    setMoveAnnouncement(
      `Task ${movedItem.title || 'Untitled task'} moved to position ${toIndex + 1} of ${currentPhaseList.length}`,
    );
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    moveAction(draggedIndex, targetIndex);
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-6">
      {/* Load failures — distinct from a genuinely unconfigured program */}
      {loadError && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
          <span>Couldn't load phase actions — {loadError}</span>
          <button
            type="button"
            onClick={fetchActions}
            className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 font-bold hover:bg-rose-100 transition-colors cursor-pointer shrink-0"
          >
            Retry
          </button>
        </div>
      )}
      {pickerError && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-700">
          <span>Couldn't load automations / email templates — {pickerError} Pickers may be empty.</span>
          <button
            type="button"
            onClick={fetchAutomationsAndTemplates}
            className="px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-amber-700 font-bold hover:bg-amber-100 transition-colors cursor-pointer shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Phases Navigation Strip */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 border-b border-slate-200">
        {STANDARD_PHASES.map((p) => {
          const count = actions.filter((a) => a.phaseKey === p.key).length;
          const isActive = activePhaseKey === p.key;

          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setActivePhaseKey(p.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? 'bg-brand-primary text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/70'
              }`}
            >
              <span>{p.label}</span>
              {count > 0 && (
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Action Configuration Area */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">
              Administrative Actions for {STANDARD_PHASES.find((p) => p.key === activePhaseKey)?.label}
            </h3>
            <p className="text-xs text-slate-500">
              Configure required or optional actions that admins must complete in this phase.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setTargetPhaseKey(activePhaseKey);
                setSelectedSourcePhaseKey(activePhaseKey);
                setSelectedSourceProgramId('');
                setCopyError(null);
                setIsCopyModalOpen(true);
              }}
              className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-2xs"
            >
              <Copy className="h-3.5 w-3.5 text-indigo-600" />
              <span>Copy Tasks...</span>
            </button>

            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleAddAction(e.target.value as PhaseActionType);
                  e.target.value = '';
                }
              }}
              defaultValue=""
              className="px-3 py-1.5 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-bold cursor-pointer"
            >
              <option value="" disabled>
                + Add Phase Action...
              </option>
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.type} value={opt.type}>
                  {opt.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 rounded-xl bg-brand-primary text-white text-xs font-extrabold shadow-xs hover:bg-brand-primary-dark cursor-pointer"
            >
              {saving ? 'Saving...' : 'Save Phase Config'}
            </button>
          </div>
        </div>

        {/* Stage Nudge Configuration Section */}
        <div className="p-4 rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50/70 to-blue-50/40 shadow-2xs space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-brand-primary text-white shadow-xs">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 tracking-tight">
                  Workflow Stage Nudge Configuration
                </h4>
                <p className="text-[11px] font-medium text-slate-500">
                  Select the email template and optional document template triggered from the Workflow Command Center for applicants in {STANDARD_PHASES.find((p) => p.key === activePhaseKey)?.label}.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="space-y-1">
              <label className="block text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">
                Stage Nudge Email Template *
              </label>
              <SearchableSelect
                value={stageNudges[activePhaseKey]?.emailTemplateId || ''}
                onChange={(v) =>
                  setStageNudges((prev) => ({
                    ...prev,
                    [activePhaseKey]: { ...prev[activePhaseKey], emailTemplateId: v },
                  }))
                }
                options={emailTemplatesList.map((t) => ({ value: t.id, label: t.name }))}
                placeholder="Search email templates..."
                clearLabel="Select Stage Nudge Email Template..."
                emptyLabel="No email templates found"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">
                Attached Document Template (Optional)
              </label>
              <SearchableSelect
                value={stageNudges[activePhaseKey]?.documentTemplateId || ''}
                onChange={(v) =>
                  setStageNudges((prev) => ({
                    ...prev,
                    [activePhaseKey]: { ...prev[activePhaseKey], documentTemplateId: v },
                  }))
                }
                options={documentTemplatesList.map((d) => ({
                  value: d.id,
                  label: d.code ? `[${d.code}] ${d.name}` : d.name,
                }))}
                placeholder="Search document templates..."
                clearLabel="None (No document template attached)"
                emptyLabel="No document templates found"
              />
            </div>
          </div>
        </div>

        {/* ADM-11: reorder moves are announced to assistive technology */}
        <p className="sr-only" role="status" aria-live="polite">{moveAnnouncement}</p>

        {/* Action Item Cards */}
        <div className="space-y-3">
          {phaseActions.map((act, index) => {
            const Icon = ACTION_TYPE_OPTIONS.find((o) => o.type === act.actionType)?.icon || Info;
            const otherTasksInProgram = actions.filter((a) => a.id !== act.id);

            return (
              <div
                key={act.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`p-4 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-3 transition-all ${
                  draggedIndex === index ? 'opacity-40 border-dashed border-indigo-400' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div
                      className="p-1.5 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing shrink-0"
                      title="Drag to reorder task"
                    >
                      <GripVertical className="h-4 w-4" />
                    </div>
                    <div className="flex items-center shrink-0">
                      <button
                        type="button"
                        aria-label={`Move "${act.title || 'Untitled task'}" up`}
                        title="Move task up"
                        disabled={index === 0}
                        onClick={() => moveAction(index, index - 1)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move "${act.title || 'Untitled task'}" down`}
                        title="Move task down"
                        disabled={index === phaseActions.length - 1}
                        onClick={() => moveAction(index, index + 1)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <input
                      type="text"
                      value={act.title}
                      onChange={(e) => handleUpdateAction(act.id, { title: e.target.value })}
                      placeholder="Action Title"
                      className="text-xs font-extrabold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 outline-none px-1 py-0.5 flex-1 min-w-0"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={act.required}
                        onChange={(e) => handleUpdateAction(act.id, { required: e.target.checked })}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-brand-primary h-4 w-4"
                      />
                      <span>Required Gate</span>
                    </label>

                    <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={act.requireNote || act.config?.requireNote || false}
                        onChange={(e) =>
                          handleUpdateAction(act.id, {
                            requireNote: e.target.checked,
                            config: { ...act.config, requireNote: e.target.checked },
                          })
                        }
                        className="rounded border-slate-300 text-indigo-600 focus:ring-brand-primary h-4 w-4"
                      />
                      <span>Mandatory Note</span>
                    </label>

                    <button aria-label="Remove task" title="Remove task"
                      type="button"
                      onClick={() => handleRemoveAction(act.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Specific Parameter Form Fields */}
                <div className="pl-9 space-y-2 text-xs">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Instructions / Procedural Guidance
                    </label>
                    <input
                      type="text"
                      value={act.instructions || ''}
                      onChange={(e) => handleUpdateAction(act.id, { instructions: e.target.value })}
                      placeholder="e.g. Admin: Manually enter required details and save..."
                      className="w-full px-3 py-1.5 rounded-xl border border-slate-200 text-xs"
                    />
                  </div>

                  {/* Task Blocker / Prerequisite Configuration */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Task Blockers / Prerequisites (Must be completed first)
                    </label>
                    <SearchableSelect
                      value=""
                      onChange={(v) => {
                        if (!v) return;
                        const currentPrereqs = act.prerequisiteActionDefIds || [];
                        if (!currentPrereqs.includes(v)) {
                          handleUpdateAction(act.id, {
                            prerequisiteActionDefIds: [...currentPrereqs, v],
                          });
                        }
                      }}
                      options={otherTasksInProgram
                        .filter((other) => !(act.prerequisiteActionDefIds || []).includes(other.id))
                        .map((other) => {
                          const phaseLabel = STANDARD_PHASES.find((p) => p.key === other.phaseKey)?.label || other.phaseKey;
                          return {
                            value: other.id,
                            label: `[${phaseLabel}] ${other.title}`,
                          };
                        })}
                      placeholder="Select prerequisite task to block this action…"
                      clearLabel="Add prerequisite task…"
                      emptyLabel="No available prerequisite tasks"
                    />

                    {(act.prerequisiteActionDefIds || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(act.prerequisiteActionDefIds || []).map((prereqId) => {
                          const prereqDef = actions.find((a) => a.id === prereqId);
                          const phaseLabel = prereqDef ? (STANDARD_PHASES.find((p) => p.key === prereqDef.phaseKey)?.label || prereqDef.phaseKey) : 'Unknown Phase';

                          return (
                            <span
                              key={prereqId}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-bold"
                            >
                              <span>Blocked by: [{phaseLabel}] {prereqDef?.title || 'Unknown Task'}</span>
                              <button aria-label="Remove prerequisite" title="Remove prerequisite"
                                type="button"
                                onClick={() => {
                                  const updated = (act.prerequisiteActionDefIds || []).filter((id) => id !== prereqId);
                                  handleUpdateAction(act.id, { prerequisiteActionDefIds: updated });
                                }}
                                className="text-amber-600 hover:text-amber-900 rounded p-0.5 hover:bg-amber-100 cursor-pointer"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Conditional Visibility — "Show this task only when…".
                      The value picker is populated DYNAMICALLY from the
                      chosen source task's CURRENT configured options:
                      the condition stores option values (ids / yes-no
                      keys), never label text, because labels are
                      editable per task. */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Show This Task Only When… (Optional)
                    </label>
                    {(() => {
                      const allDefsById = new Map(actions.map((a) => [a.id, a]));
                      const currentSource = act.showWhen
                        ? actions.find((a) => a.id === act.showWhen!.sourceActionDefId)
                        : undefined;
                      const eligibleSources = actions.filter(
                        (a) =>
                          a.id !== act.id &&
                          a.phaseKey === act.phaseKey &&
                          (a.actionType === 'select_one_option' || a.actionType === 'yes_no') &&
                          !a.showWhen &&
                          (a.sortOrder || 0) < (act.sortOrder || 0)
                      );
                      const sourceOptions = [
                        ...eligibleSources,
                        ...(currentSource && !eligibleSources.some((a) => a.id === currentSource.id)
                          ? [currentSource]
                          : []),
                      ];
                      const sourceAnswers = currentSource ? showWhenAnswerValues(currentSource) : null;
                      const problem = act.showWhen ? showWhenProblemFor(act, allDefsById) : null;
                      const preview = describeShowWhen(act, currentSource);

                      return (
                        <div className="space-y-2">
                          <SearchableSelect
                            value={act.showWhen?.sourceActionDefId || ''}
                            onChange={(v) => {
                              if (!v) {
                                handleUpdateAction(act.id, { showWhen: undefined });
                              } else {
                                handleUpdateAction(act.id, {
                                  showWhen: { sourceActionDefId: v, values: [] },
                                });
                              }
                            }}
                            options={sourceOptions.map((a) => ({ value: a.id, label: a.title }))}
                            placeholder="Select the decision task…"
                            clearLabel="Always shown (no condition)"
                            emptyLabel="No earlier decision task in this phase"
                          />

                          {act.showWhen && currentSource && sourceAnswers && (
                            <div className="flex flex-wrap gap-1.5">
                              {sourceAnswers.map(({ value, label }) => {
                                const checked = (act.showWhen!.values || []).includes(value);
                                return (
                                  <label
                                    key={value}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-bold cursor-pointer ${
                                      checked
                                        ? 'bg-indigo-50 border-indigo-300 text-indigo-900'
                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const current = act.showWhen!.values || [];
                                        const next = e.target.checked
                                          ? [...current, value]
                                          : current.filter((x) => x !== value);
                                        handleUpdateAction(act.id, {
                                          showWhen: { sourceActionDefId: act.showWhen!.sourceActionDefId, values: next },
                                        });
                                      }}
                                      className="rounded border-slate-300 text-indigo-600 focus:ring-brand-primary h-3.5 w-3.5"
                                    />
                                    <span>{label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}

                          {act.showWhen && preview && !problem && (
                            <p className="text-[11px] font-medium text-slate-600">{preview}</p>
                          )}

                          {act.showWhen && problem && (
                            <div className="flex items-start gap-1.5 text-[11px] font-bold text-amber-900 bg-amber-50 px-2.5 py-1.5 rounded-lg border border-amber-200">
                              <AlertTriangle size={12} className="shrink-0 mt-0.5 text-amber-700" />
                              <span>
                                This condition needs fixing — until it is fixed the task stays visible
                                to admins (it never hides silently). {problem}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {act.actionType === 'send_email' && (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Email Template
                      </label>
                      <SearchableSelect
                        value={act.config.emailTemplateId || ''}
                        onChange={(v) =>
                          handleUpdateAction(act.id, {
                            config: { ...act.config, emailTemplateId: v },
                          })
                        }
                        options={emailTemplatesList.map((t) => ({ value: t.id, label: t.name }))}
                        placeholder="Search email templates..."
                        clearLabel="Select Email Template..."
                        emptyLabel="No email templates found"
                      />
                    </div>
                  )}

                  {act.actionType === 'select_one_option' && (
                    <div className="space-y-3 pt-2 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                          Configured Options (Admin selects one)
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const opts = act.config.options || [];
                            const newOpt = {
                              id: `opt_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
                              label: `Option ${opts.length + 1}`,
                              actionType: 'none' as const,
                            };
                            handleUpdateAction(act.id, {
                              config: { ...act.config, options: [...opts, newOpt] },
                            });
                          }}
                          className="px-2.5 py-1 text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 flex items-center gap-1 cursor-pointer"
                        >
                          <Plus size={12} /> Add Option
                        </button>
                      </div>

                      {(!act.config.options || act.config.options.length === 0) && (
                        <p className="text-[11px] text-amber-600 italic">No options configured yet. Click "+ Add Option" to add options.</p>
                      )}

                      {(act.config.options || []).map((opt, optIndex) => (
                        <div key={opt.id || optIndex} className="p-3 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-[10px] font-black text-slate-400">#{optIndex + 1}</span>
                              <input
                                type="text"
                                value={opt.label}
                                onChange={(e) => {
                                  const opts = [...(act.config.options || [])];
                                  opts[optIndex] = { ...opts[optIndex], label: e.target.value };
                                  handleUpdateAction(act.id, { config: { ...act.config, options: opts } });
                                }}
                                placeholder="Option Label (e.g. Option 1: Send Acceptance Email)"
                                className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs bg-white font-extrabold text-slate-800 flex-1"
                              />
                            </div>
                            <button aria-label="Remove option" title="Remove option"
                              type="button"
                              onClick={() => {
                                const opts = (act.config.options || []).filter((_, i) => i !== optIndex);
                                handleUpdateAction(act.id, { config: { ...act.config, options: opts } });
                              }}
                              className="p-1 text-slate-400 hover:text-rose-600 rounded cursor-pointer"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Associated Action</label>
                              <select
                                value={opt.actionType || 'none'}
                                onChange={(e) => {
                                  const opts = [...(act.config.options || [])];
                                  opts[optIndex] = { ...opts[optIndex], actionType: e.target.value as any };
                                  handleUpdateAction(act.id, { config: { ...act.config, options: opts } });
                                }}
                                className="w-full px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white cursor-pointer"
                              >
                                <option value="none">Mark Task Complete Only (No Action)</option>
                                <option value="send_email">Send Email</option>
                                <option value="send_automation">Send Automation</option>
                              </select>
                            </div>

                            {opt.actionType === 'send_email' && (
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Email Template</label>
                                <SearchableSelect
                                  value={opt.emailTemplateId || ''}
                                  onChange={(v) => {
                                    const opts = [...(act.config.options || [])];
                                    opts[optIndex] = { ...opts[optIndex], emailTemplateId: v };
                                    handleUpdateAction(act.id, { config: { ...act.config, options: opts } });
                                  }}
                                  options={emailTemplatesList.map((t) => ({ value: t.id, label: t.name }))}
                                  placeholder="Search email templates..."
                                  clearLabel="Select Email Template..."
                                  emptyLabel="No email templates found"
                                />
                              </div>
                            )}

                            {opt.actionType === 'send_automation' && (
                              <div>
                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Automation Flow</label>
                                <SearchableSelect
                                  value={opt.automationId || ''}
                                  onChange={(v) => {
                                    const opts = [...(act.config.options || [])];
                                    opts[optIndex] = { ...opts[optIndex], automationId: v };
                                    handleUpdateAction(act.id, { config: { ...act.config, options: opts } });
                                  }}
                                  options={automationsList.map((a) => ({
                                    value: a.id,
                                    label: a.status === 'published' ? a.name : `${a.name} (${a.status || 'draft'})`,
                                    disabled: a.status !== 'published',
                                  }))}
                                  placeholder="Search automation flows..."
                                  clearLabel="Select Automation Flow..."
                                  emptyLabel="No automation flows found"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {act.actionType === 'yes_no' && (
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Yes Option Label</label>
                        <input
                          type="text"
                          value={act.config.yesLabel || ''}
                          onChange={(e) =>
                            handleUpdateAction(act.id, {
                              config: { ...act.config, yesLabel: e.target.value },
                            })
                          }
                          placeholder="Yes (default)"
                          className="w-full px-3 py-1.5 rounded-xl border border-slate-200 text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">No Option Label</label>
                        <input
                          type="text"
                          value={act.config.noLabel || ''}
                          onChange={(e) =>
                            handleUpdateAction(act.id, {
                              config: { ...act.config, noLabel: e.target.value },
                            })
                          }
                          placeholder="No (default)"
                          className="w-full px-3 py-1.5 rounded-xl border border-slate-200 text-xs"
                        />
                      </div>
                    </div>
                  )}

                  {act.actionType === 'date_entry' && (
                    <div className="pt-1">
                      <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={act.config.requireDate !== false}
                          onChange={(e) =>
                            handleUpdateAction(act.id, {
                              config: { ...act.config, requireDate: e.target.checked },
                            })
                          }
                          className="rounded border-slate-300 text-indigo-600 focus:ring-brand-primary h-4 w-4"
                        />
                        <span>Require Valid Date Input</span>
                      </label>
                    </div>
                  )}

                  {act.actionType === 'send_automation' && (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Automation Flow
                      </label>
                      <SearchableSelect
                        value={act.config.automationId || ''}
                        onChange={(v) =>
                          handleUpdateAction(act.id, {
                            config: { ...act.config, automationId: v },
                          })
                        }
                        options={automationsList.map((a) => {
                          // Picker parity with assignment: only PUBLISHED
                          // automations can run (the engine refuses drafts
                          // and archived flows at completion time), so a
                          // draft is shown for discoverability but cannot
                          // be newly bound — binding one used to set up a
                          // guaranteed refusal when the task completed.
                          const isPublished = a.status === 'published';
                          return {
                            value: a.id,
                            label: isPublished ? a.name : `${a.name} (${a.status || 'draft'} — publish to run)`,
                            disabled: !isPublished,
                          };
                        })}
                        placeholder="Search automation flows..."
                        clearLabel="Select Published Automation Flow..."
                        emptyLabel="No automation flows found"
                      />
                      {(() => {
                        const bound = automationsList.find((a) => a.id === act.config.automationId);
                        if (bound && bound.status !== 'published') {
                          return (
                            <p className="text-[11px] text-amber-700 font-medium mt-1">
                              This flow is a {bound.status || 'draft'} — completing this task will refuse
                              to run it until the automation is published. Publish it from the Automations
                              page, or choose a published flow.
                            </p>
                          );
                        }
                        if (automationsList.length > 0 && !automationsList.some((a) => a.status === 'published')) {
                          return (
                            <p className="text-[11px] text-slate-500 font-medium mt-1">
                              No published automation flows yet — publish one from the Automations page to
                              bind it here.
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}

                  {act.actionType === 'visual_text' && (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                        Visual Text Block
                      </label>
                      <textarea
                        rows={3}
                        value={act.config.displayText || ''}
                        onChange={(e) =>
                          handleUpdateAction(act.id, {
                            config: { ...act.config, displayText: e.target.value },
                          })
                        }
                        placeholder="Enter procedural checklist instructions or guidelines..."
                        className="w-full px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-mono"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {phaseActions.length === 0 && !loadError && (
            <div className="p-8 border border-dashed border-slate-200 rounded-2xl text-center bg-slate-50 text-slate-500 text-xs">
              No administrative actions configured for this phase. Click "+ Add Phase Action" to create one.
            </div>
          )}
        </div>
      </div>

      {/* Copy Phase Actions Modal */}
      {isCopyModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100">
                  <Copy className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Copy Admin Phase Tasks</h3>
                  <p className="text-xs text-slate-500">Duplicate tasks from another program into this configuration</p>
                </div>
              </div>
              <button aria-label="Close" title="Close"
                type="button"
                onClick={() => setIsCopyModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 text-xs">
              {copyError && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 font-semibold text-xs">
                  {copyError}
                </div>
              )}

              {/* Source Program Picker */}
              <div className="space-y-1">
                <label className="block text-xs font-extrabold text-slate-800">Source Program *</label>
                <SearchableSelect
                  value={selectedSourceProgramId}
                  onChange={(v) => {
                    setSelectedSourceProgramId(v);
                    setCopyError(null);
                  }}
                  options={allPrograms
                    .filter((p) => p.id !== programId)
                    .map((p) => ({
                      value: p.id,
                      label: `${p.code ? `[${p.code}] ` : ''}${p.name}`,
                    }))}
                  placeholder="Search programs…"
                  clearLabel="Select source program to copy tasks from..."
                  emptyLabel="No programs found"
                />
              </div>

              {/* Scope Selection: All Stages vs Single Stage */}
              <div className="space-y-1.5">
                <label className="block text-xs font-extrabold text-slate-800">Copy Scope</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCopyScope('all')}
                    className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                      copyScope === 'all'
                        ? 'border-indigo-600 bg-indigo-50/60 text-indigo-900 font-extrabold shadow-2xs'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-xs">All Stages</div>
                    <div className="text-[11px] font-normal text-slate-500 mt-0.5">Copy all tasks from all 8 phases</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCopyScope('single')}
                    className={`p-3 rounded-xl border text-left cursor-pointer transition-all ${
                      copyScope === 'single'
                        ? 'border-indigo-600 bg-indigo-50/60 text-indigo-900 font-extrabold shadow-2xs'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-xs">Single Stage</div>
                    <div className="text-[11px] font-normal text-slate-500 mt-0.5">Copy tasks from one specific stage</div>
                  </button>
                </div>
              </div>

              {/* Single Stage Dropdowns */}
              {copyScope === 'single' && (
                <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="space-y-1">
                    <label className="block text-[11px] font-bold text-slate-600 uppercase">From Source Stage</label>
                    <select
                      value={selectedSourcePhaseKey}
                      onChange={(e) => setSelectedSourcePhaseKey(e.target.value as StandardPhaseKey)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs bg-white"
                    >
                      {STANDARD_PHASES.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[11px] font-bold text-slate-600 uppercase">Into Target Stage</label>
                    <select
                      value={targetPhaseKey}
                      onChange={(e) => setTargetPhaseKey(e.target.value as StandardPhaseKey)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs bg-white"
                    >
                      {STANDARD_PHASES.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Strategy: Replace vs Append */}
              <div className="space-y-1.5">
                <label className="block text-xs font-extrabold text-slate-800">Copy Strategy</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="copyMode"
                      value="replace"
                      checked={copyMode === 'replace'}
                      onChange={() => setCopyMode('replace')}
                      className="text-indigo-600 focus:ring-brand-primary"
                    />
                    <span>Replace existing tasks {copyScope === 'all' ? 'in all phases' : 'in target phase'}</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700">
                    <input
                      type="radio"
                      name="copyMode"
                      value="append"
                      checked={copyMode === 'append'}
                      onChange={() => setCopyMode('append')}
                      className="text-indigo-600 focus:ring-brand-primary"
                    />
                    <span>Append to existing tasks</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => setIsCopyModalOpen(false)}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteCopy}
                disabled={copying || !selectedSourceProgramId}
                className="px-4 py-2 rounded-xl bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 text-white text-xs font-extrabold cursor-pointer"
              >
                {copying ? 'Copying...' : 'Copy Tasks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
